import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { registerUser, logout, getCollaborateEULAContent, createCollaborateUserEULA } from '../../actions/users'
import _, { debounce } from 'lodash'
import {
  Container,
  Box,
  Notification,
  NotificationDesc,
  NotificationTitle,
  Modal,
  Textfield,
  Typography,
  Password,
  SwxTheme, Button
} from 'swx-atomic-component-lib'
import TempCodeVerification from '../tempCodeVerifcation'
import i18n from '../../i18n'
import ExclamationMarkCircleIcon from 'calcite-ui-icons-react/ExclamationMarkCircleIcon'
import ViewHideIcon from 'calcite-ui-icons-react/ViewHideIcon'
import ViewMixedIcon from 'calcite-ui-icons-react/ViewMixedIcon'
import CheckIcon from 'calcite-ui-icons-react/CheckIcon'
import {
  collaborateTheme,
  errorMessageStyle,
  errorIconStyle,
  buttonStyle,
  ctaContainer,
  buttonCancelStyle,
  buttonPrimaryStyle,
  smallScreenButtonContainer,
  buttonPrimaryAcceptedStyle
} from './style'
import queryStringFunc from '../../helpers/query-string'
import PasswordStrengthMeterCarbon from '../profile/PasswordStrengthMeterCarbon'
import ReCAPTCHA from 'react-google-recaptcha'
import ButtonWithSpinner from '../utils/ButtonWithSpinner'
import '@ibm/plex/css/ibm-plex.css'

const userStateInit = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  reCaptcha: ''
}

const { x3 } = SwxTheme.sizing

const userKeys = Object.keys(userStateInit)

function RegisterComponent (props) {
  const [error, setError] = useState({})
  const [user, setUser] = useState(userStateInit)
  const [queryString, setQueryString] = useState({})
  const [score, setScore] = useState(1)
  const [zxcvbnResult, setZxcvbnResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isVerification, setIsVerification] = useState(false)
  const [isTermsOpen, setIsTermsOpen] = useState(false)
  const [eulaContent, setEulaContent] = useState(undefined)
  const [eulaId, setEulaId] = useState(undefined)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [eulaAcceptanceDateTime, setEulaAcceptanceDateTime] = useState(undefined)
  const [errors, setErrors] = useState({})
  const [passwordError, setPasswordError] = useState()
  const isSignUpDisable = userKeys.some((key) => errors[key] !== '') || passwordError
  const isPending = isLoading || isSignUpDisable

  const {
    registerUser,
    logout,
    history
  } = props

  useEffect(() => {
    const qs = queryStringFunc()

    if (!qs.clientId) {
      history.push('/')
    }

    setQueryString(qs)

    logout()
    getCollaborateEULAContent()
      .then(response => {
        setEulaContent(response.data.content)
        setEulaId(response.data.id)
      })
      .catch(err => console.error('EULA content', err.toString()))
  }, [])

  useEffect(() => {
    if (!_.isEmpty(error)) {
      const { status, statusText, data = {} } = error

      console.error(`${statusText}: ${JSON.stringify(data.msg)}`)

      switch (status) {
        case 403:
          history.push('/')
          break
        case 400:
          break
        case 500:
          break
        default:
      }
    }
  }, [error])

  useEffect(() => {
    if (isTermsAccepted && eulaAcceptanceDateTime && !isPending) {
      finishRegistration()
    }
  }, [isTermsAccepted, eulaAcceptanceDateTime])

  useEffect(() => {
    if (user.confirmPassword !== '' && user.password !== user.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: i18n.signUpPage.error.confirmPasswordMatch }))
    } else if (user.confirmPassword !== '' && user.password === user.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: '' }))
    }
  }, [user.password, user.confirmPassword])

  const onPasswordChange = useCallback((name) => debounce((val) => {
    const isPassword = name === 'password'
    const { password: newVal, score, zxcvbnResult, isValid } = val
    let errMsg = ''
    setUser(prev => {
      if (!newVal) {
        errMsg = isPassword ? i18n.signUpPage.error.passwordRequired : i18n.signUpPage.error.confirmPasswordRequired
      } else if (newVal.length < 12) {
        errMsg = i18n.signUpPage.error.passwordMin12
      }

      setErrors(prev => ({ ...prev, [name]: errMsg }))
      return { ...prev, [name]: newVal }
    })

    if (isPassword) {
      setScore(score)
      setZxcvbnResult(zxcvbnResult)
      setPasswordError(!isValid)
    }
  }, 500), [])

  const onChange = useCallback((name) => debounce((oriValue, elem) => {
    let errorMsg = ''
    const value = oriValue.trim()
    switch (name) {
      case 'firstName':
      case 'lastName':
        if (!value) {
          errorMsg = name === 'firstName' ? i18n.signUpPage.error.firstNameRequired : i18n.signUpPage.error.lastNameRequired
        } else if (value.length > 100) {
          errorMsg = i18n.signUpPage.error.max100
        } else if (value.length < 2) {
          errorMsg = i18n.signUpPage.error.min2
        }
        break

      case 'email':
        if (!value) {
          errorMsg = i18n.signUpPage.error.emailRequired
        } else if (!elem.validated) {
          errorMsg = i18n.signUpPage.error.emailInvalid
        }
        break

      default:
        break
    }

    setErrors(prev => ({ ...prev, [name]: errorMsg }))
    setUser(prev => ({ ...prev, [name]: value }))
  }, 500), [])

  const onChangeCaptcha = useCallback((val) => setErrors(prev => ({ ...prev, reCaptcha: val && val.length > 0 ? '' : false })), [])

  function onSubmit (e) {
    if (isPending === true) {
      return
    }

    if (!isTermsAccepted) {
      return onClickTerms()
    }

    finishRegistration()
  }

  function finishRegistration () {
    const { firstName, lastName, email, password, confirmPassword } = user
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const data = {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      password_confirmation: confirmPassword,
      timezone,
      client_id: queryString.clientId
    }

    setIsLoading(true)
    setIsTermsAccepted(false)
    setError({})

    setTimeout(() => {
      registerUser(data).then(results => {
        if (results.action.type === 'REGISTER_USER_FULFILLED' && results.value.data.is_verified === false) {
          createCollaborateUserEULA(results.value.data.id, eulaId, eulaAcceptanceDateTime)
        }
        setIsLoading(false)
        setEmail(email)
        setIsVerification(true)
        setIsModalOpen(true)

        window.parent.postMessage({ action: 'codeVerificationOpen' }, process.env.REACT_APP_POSTMESSAGE_WHITELIST_URL)
      }, err => {
        setIsLoading(false)
        setError(err.response || {})
      })
    }, 1000)
  }

  function closeModal () {
    setIsModalOpen(false)
    setIsVerification(false)
    setIsTermsOpen(false)
    window.parent.postMessage({ action: 'codeVerificationClose' }, process.env.REACT_APP_POSTMESSAGE_WHITELIST_URL)
  }

  function onClickTerms () {
    setIsTermsOpen(true)
    setIsModalOpen(true)
    window.parent.postMessage({ action: 'termsOpen' }, process.env.REACT_APP_POSTMESSAGE_WHITELIST_URL)
  }

  function onClickAcceptButton () {
    setIsTermsAccepted(true)
    setEulaAcceptanceDateTime(new Date())
    closeModal()
  }

  function onClickPrivacy () {
    window.open(process.env.REACT_APP_PRIVACY_POLICY_URL)
  }

  function isMobile () {
    return (window.screen.width < 600)
  }

  const renderTextFields = useCallback(({
    name, tabIndex, type = 'text', errorMessage
  }) => {
    const msg = errorMessage[name]
    return (
      <Box className='field-container'>
        <Typography className='field-label'>{i18n.signUpPage.placeholder[name]}</Typography>
        <Textfield
          type={type}
          id={name}
          name={name}
          onChange={onChange(name)}
          error={msg}
          message={msg}
          tabIndex={tabIndex}
          validationProps={{ customStyle: errorMessageStyle }}
          iconProps={{ customStyle: `${errorIconStyle} ${msg && 'display: flex'}` }}
          className={msg && 'error'}
          icon={<ExclamationMarkCircleIcon />}
        />
      </Box>
    )
  }, [onChange])

  const renderPassword = useCallback(
    ({
      name, errorMessage, strengthCheck = false, placeholder = '', isError = false
    }) => {
      const msg = errorMessage[name]
      const hasError = msg || isError
      return (
        <Box className='field-container'>
          <Typography className='field-label'>{i18n.signUpPage.placeholder[name]}</Typography>
          <Password
            id={name}
            name={name}
            onChange={onPasswordChange(name)}
            error={msg}
            message={msg}
            strengthCheck={strengthCheck}
            placeholder={placeholder}
            minScore={3}
            validationProps={{ customStyle: errorMessageStyle }}
            styledToggleProps={{
              type: 'button',
              className: 'toggle-style'
            }}
            hasIcon={false}
            fakePlaceholder={false}
            customVisibleIcon={ViewMixedIcon}
            customHideIcon={ViewHideIcon}
            className={hasError && 'error'}
            noExtraIcon={!hasError}
            extraIcon={
              <div className='pass-error'>
                <ExclamationMarkCircleIcon style={{ top: `calc(50% - ${msg ? 10 : 0}px)` }} />
              </div>
          }
          />
        </Box>
      )
    },
    [onPasswordChange]
  )

  return (
    <Container customStyle={collaborateTheme}>
      <Box className='sign-in-container'>
        <span>{i18n.registerPage.info}? </span>
        <a
          href='#top' onClick={() => {
            window.parent.postMessage({ action: 'login' }, process.env.REACT_APP_POSTMESSAGE_WHITELIST_URL)
            history.push(`/login?clientId=${queryString.clientId}`)
          }}
        >
          {i18n.signUpPage.signInHere}
        </a>
      </Box>
      {renderTextFields({ name: 'firstName', tabIndex: 1, errorMessage: errors })}
      {renderTextFields({ name: 'lastName', tabIndex: 2, errorMessage: errors })}
      {renderTextFields({ name: 'email', type: 'email', tabIndex: 3, errorMessage: errors })}
      {renderPassword({
        name: 'password',
        placeholder: i18n.signUpPage.placeholder.setAStrongPassword,
        errorMessage: errors,
        strengthCheck: true,
        isError: passwordError
      })}
      {renderPassword({
        name: 'confirmPassword',
        errorMessage: errors
      })}

      <PasswordStrengthMeterCarbon
        password={user.password}
        score={score}
        zxcvbnResult={zxcvbnResult}
        minScore={3}
      />

      <Box className='field-container captcha'>
        <ReCAPTCHA
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={onChangeCaptcha}
        />
      </Box>

      <Box display='flex' className='field-container-p'>
        <Box>
          <ButtonWithSpinner
            isLoading={isLoading}
            value={i18n.signUpPage.signUp}
            disabled={isPending}
            onClick={onSubmit}
            customStyle={buttonStyle}
          />
        </Box>
        <Box className='links' alignItems='center' display='flex'>
          <a href='#top' onClick={onClickTerms}>Agree terms</a>
          <a href='#top' onClick={onClickPrivacy}>Privacy</a>
        </Box>
      </Box>
      {
        !_.isEmpty(error) && (
          <Notification type='error' customStyle={notificationStyle}>
            <NotificationTitle>{i18n.signUpPage.notification.error.title}</NotificationTitle>
            <NotificationDesc>{JSON.stringify(error.data.msg)}</NotificationDesc>
          </Notification>
        )
      }
      <Modal
        customStyle='overflow:hidden'
        open={isModalOpen}
        modalStyle={{
          width: '100%',
          maxWidth: '100vw',
          maxHeight: '55vh',
          position: 'absolute',
          top: 0,
          left: 0
        }}
      >
        <Box display='flex'>
          {
            isVerification && <TempCodeVerification
              email={email}
              closeModal={closeModal}
              isCollaborate
              module='register'
              needVerify
              additionalParam={{ is_collaborate_self_register: true }}
                              />
          }
          {
            isTermsOpen &&
              <div><Box customStyle={divider} /><Box customStyle={contentContainer} p={x3} dangerouslySetInnerHTML={{ __html: eulaContent || '' }} />
                <Box customStyle={ctaContainer}>
                  <Box customStyle={isMobile() ? smallScreenButtonContainer : ''}>
                    <Button
                      ghost
                      customStyle={buttonCancelStyle}
                      onClick={closeModal}
                    >
                      {i18n.common.button.cancel}
                    </Button>
                    {
                    !isTermsAccepted &&
                      <Button
                        customStyle={buttonPrimaryStyle}
                        onClick={onClickAcceptButton}
                        disabled={isTermsAccepted}
                      >
                        {i18n.common.button.accept}
                      </Button>
                  }
                  </Box>
                  {
                  isTermsAccepted &&
                    <Button
                      customStyle={buttonPrimaryAcceptedStyle}
                    >
                      <CheckIcon />
                    </Button>
                }
                </Box>
              </div>
          }
        </Box>
      </Modal>
    </Container>
  )
}

function mapStateToProps (state) {
  return {}
}

const notificationStyle = `
  position: fixed;
  top: 48px;
  left: 0px;
  width: 100%;
  
  @media only screen and (max-width: 600px) {
    top: 0px;
  }
`

const contentContainer = `
    font-size: 13px;
    padding: 0 2rem 0 2rem;
`
const divider = `
  width: 100%;
  height: 0.063rem;
  margin: 0 0 0.25rem;
  background-color: #c6c6c6;
  position: fixed;
  left: 0;
  top 0;
`

export default connect(mapStateToProps, { registerUser, logout })(RegisterComponent)
